<template>
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    @click.self="handleClose"
  >
    <div class="bg-white rounded-xl p-6 max-w-md w-full">
      <h2 class="text-xl font-bold mb-4">{{ $t('changePassword.title') }}</h2>

      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
        {{ errorMessage }}
      </div>

      <!-- Form -->
      <form v-if="!successMessage" @submit.prevent="handleSubmit">
        <div class="space-y-4">
          <div>
            <label for="currentPassword" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('changePassword.currentPassword') }}
            </label>
            <input
              id="currentPassword"
              v-model="currentPassword"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
              autocomplete="current-password"
            />
          </div>

          <div>
            <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('changePassword.newPassword') }}
            </label>
            <input
              id="newPassword"
              v-model="newPassword"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
              autocomplete="new-password"
            />
          </div>

          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('changePassword.confirmPassword') }}
            </label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
              autocomplete="new-password"
            />
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button
            type="button"
            @click="handleClose"
            class="btn btn-secondary flex-1"
            :disabled="loading"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            type="submit"
            class="btn btn-primary flex-1"
            :disabled="loading"
          >
            {{ loading ? $t('changePassword.changing') : $t('changePassword.button') }}
          </button>
        </div>
      </form>

      <!-- Close button after success -->
      <div v-else class="mt-4">
        <button
          @click="handleClose"
          class="btn btn-primary w-full"
        >
          {{ $t('common.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '../composables/useAuth'

const emit = defineEmits(['close'])
const { t } = useI18n()
const { changePassword } = useAuth()

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const handleSubmit = async () => {
  errorMessage.value = ''
  successMessage.value = ''

  // Client-side validation
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = t('changePassword.passwordMismatch')
    return
  }

  if (newPassword.value.length < 8) {
    errorMessage.value = t('changePassword.passwordTooShort')
    return
  }

  loading.value = true

  try {
    const result = await changePassword(
      currentPassword.value,
      newPassword.value,
      confirmPassword.value
    )

    if (result.success) {
      successMessage.value = result.message || t('changePassword.successMessage')
      // Clear form
      currentPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''

      // Auto-close after 2 seconds
      setTimeout(() => {
        handleClose()
      }, 2000)
    } else {
      errorMessage.value = result.error
    }
  } finally {
    loading.value = false
  }
}

const handleClose = () => {
  emit('close')
}
</script>
